// Per-campaign slice state, persisted across tab switches via a zustand store
// keyed by campaignId. The store initialises from a `CampaignConfig.inputs`
// array the first time the hook is called for a given campaign.

import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type { AxisSpec, SliceState } from '../types';

type CampaignKey = string;

type SliceStore = {
  byCampaign: Record<CampaignKey, SliceState>;
  ensure: (id: CampaignKey, inputs: readonly AxisSpec[]) => void;
  update: (id: CampaignKey, patch: Partial<SliceState>) => void;
  replace: (id: CampaignKey, next: SliceState) => void;
};

const midpoint = (axis: AxisSpec): number => {
  if (axis.mode === 'fixed') return axis.fixedValue ?? 0;
  if (axis.min === undefined || axis.max === undefined) return 0;
  return 0.5 * (axis.min + axis.max);
};

/**
 * Build the default SliceState for a campaign: pick the first two
 * range-mode inputs as x/y, pin the remainder at their midpoint, and use a
 * 10% slab thickness.
 */
export function buildDefaultSliceState(inputs: readonly AxisSpec[]): SliceState {
  const rangeInputs = inputs.filter((a) => a.mode === 'range');
  const xAxis = rangeInputs[0]?.name ?? '';
  const yAxis = rangeInputs[1]?.name ?? '';
  const pinned: Record<string, number> = {};
  for (const axis of rangeInputs) {
    if (axis.name === xAxis || axis.name === yAxis) continue;
    pinned[axis.name] = midpoint(axis);
  }
  return {
    xInput: xAxis,
    yInput: yAxis,
    pinned,
    slabFraction: 0.1,
  };
}

const useSliceStore = create<SliceStore>((set) => ({
  byCampaign: {},
  ensure: (id, inputs) =>
    set((s) => {
      // Wait until the caller actually has axis metadata; otherwise we'd
      // persist a blank { xInput: '', yInput: '' } slice which then sticks
      // because of the existence check below, causing surface queries to
      // fail with "xInput/yInput required" and the panel to hang on
      // "Loading surface…" after campaignState eventually lands.
      const hasAxes = inputs.some((a) => a.mode === 'range');
      if (!hasAxes) return s;
      const existing = s.byCampaign[id];
      if (existing && existing.xInput) return s;
      return {
        ...s,
        byCampaign: { ...s.byCampaign, [id]: buildDefaultSliceState(inputs) },
      };
    }),
  update: (id, patch) =>
    set((s) => {
      const current = s.byCampaign[id];
      if (!current) return s;
      const next: SliceState = {
        ...current,
        ...patch,
        pinned: { ...current.pinned, ...(patch.pinned ?? {}) },
      };
      return { ...s, byCampaign: { ...s.byCampaign, [id]: next } };
    }),
  replace: (id, next) =>
    set((s) => ({ ...s, byCampaign: { ...s.byCampaign, [id]: next } })),
}));

export type UseSliceStateResult = {
  slice: SliceState;
  setSlice: (next: SliceState) => void;
  patchSlice: (patch: Partial<SliceState>) => void;
};

export function useSliceState(
  campaignId: string,
  inputs: readonly AxisSpec[],
): UseSliceStateResult {
  const ensure = useSliceStore((s) => s.ensure);
  const replace = useSliceStore((s) => s.replace);
  const update = useSliceStore((s) => s.update);
  const stored = useSliceStore((s) => s.byCampaign[campaignId]);

  useEffect(() => {
    ensure(campaignId, inputs);
  }, [campaignId, inputs, ensure]);

  const fallback = useMemo(() => buildDefaultSliceState(inputs), [inputs]);
  const slice = stored ?? fallback;

  return {
    slice,
    setSlice: (next) => replace(campaignId, next),
    patchSlice: (patch) => update(campaignId, patch),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Study Cursor (FXC-9009 two-rooms redesign) — the ONE shared cursor every
// workbench surface subscribes to. Extends the per-campaign SliceState above:
//
//   - REKEYED by projectId+branch (not campaignId) so the slice survives stage
//     hops, campaign switches and reloads;
//   - carries the full shared-view state: slice (x/y/pins/slab), headline
//     output, evidence mode, selected sample point, transient hover crosshair
//     and the shared 3D camera (MannequinSurfaceViewState shape);
//   - persists the durable subset to localStorage per key (hover + selection
//     are transient by design — a stale runNodeId must not survive a reload);
//   - inherits the ensure()-after-axes-load guard: never initialise/persist a
//     cursor before axis metadata (>=1 range axis) exists.
//
// Every panel is a STATELESS subscriber — no per-card slice state exists, so a
// card cannot desync (design principle 3). Fetches key on studySliceKey(),
// which is deterministic (sorted pins, fixed float formatting) so react-query
// coalesces N panels × 1 slice change into one request per output.

export type StudyView3d = {
  yaw: number;
  pitch: number;
  zoom: number;
  panX: number;
  panY: number;
};

export type StudySelectedPoint = {
  /** The run node backing the point, when known (drives geometry re-rig + flow fetch). */
  runNodeId: string | null;
  /** Full N-D input vector of the point (canonical axis names, UNPREFIXED). */
  inputs: Record<string, number>;
};

export type StudyEvidenceMode = "raw" | "compatible" | "corrected";

export type StudyCursorState = SliceState & {
  /** Headline quantity — panels may override locally. */
  selectedOutput: string;
  evidenceMode: StudyEvidenceMode;
  selectedPoint: StudySelectedPoint | null;
  /** Transient mirrored crosshair, in DATA coordinates of {xInput,yInput}. */
  hoverCursor: { x: number; y: number } | null;
  /** Shared 3D camera for every 3D lane (MannequinSurfaceViewState shape). */
  view3d: StudyView3d;
};

const DEFAULT_VIEW3D: StudyView3d = { yaw: -0.6, pitch: 0.35, zoom: 1, panX: 0, panY: 0 };

/** Fields that survive a reload. hoverCursor/selectedPoint are transient. */
const PERSISTED_FIELDS = [
  "xInput", "yInput", "pinned", "slabFraction", "selectedOutput", "evidenceMode", "view3d",
] as const;
type PersistedField = (typeof PERSISTED_FIELDS)[number];
type PersistedCursor = Pick<StudyCursorState, PersistedField>;

export function studyCursorStorageKey(key: string): string {
  return `thread:v5:study-cursor:${key}`;
}

/** Canonical cursor key: one cursor per project+branch. */
export function studyCursorKey(projectId: string, branch: string): string {
  return `${projectId}::${branch}`;
}

function defaultStudyCursor(inputs: readonly AxisSpec[]): StudyCursorState {
  return {
    ...buildDefaultSliceState(inputs),
    selectedOutput: "CL",
    evidenceMode: "corrected",
    selectedPoint: null,
    hoverCursor: null,
    view3d: { ...DEFAULT_VIEW3D },
  };
}

/** First range axis that differs from `taken` — the collision fixup target.
 *  "" only when fewer than two range axes exist (a genuinely 1-D project). */
function firstOtherRangeAxis(inputs: readonly AxisSpec[], taken: string): string {
  return inputs.find((a) => a.mode === "range" && a.name !== taken)?.name ?? "";
}

/** Merge a persisted snapshot over the axis-derived defaults, dropping anything
 *  that no longer matches the live axis metadata (a cursor saved against a
 *  different campaign shape must not pin/slice on axes that don't exist). */
function hydrateStudyCursor(
  base: StudyCursorState,
  persisted: Partial<PersistedCursor> | null,
  inputs: readonly AxisSpec[],
): StudyCursorState {
  if (!persisted || typeof persisted !== "object") return base;
  const names = new Set(inputs.map((a) => a.name));
  const rangeNames = new Set(inputs.filter((a) => a.mode === "range").map((a) => a.name));
  const next: StudyCursorState = { ...base };
  if (typeof persisted.xInput === "string" && rangeNames.has(persisted.xInput)) next.xInput = persisted.xInput;
  if (
    typeof persisted.yInput === "string" &&
    rangeNames.has(persisted.yInput) &&
    persisted.yInput !== next.xInput
  ) {
    next.yInput = persisted.yInput;
  }
  // Collision fixup (adversarial-review fix): NEVER collapse yInput to "" when
  // another range axis exists — a persisted {x: <base.y>} used to zero the y
  // axis permanently, killing every Explore surface for the project.
  if (next.yInput === next.xInput) next.yInput = firstOtherRangeAxis(inputs, next.xInput);
  if (persisted.pinned && typeof persisted.pinned === "object") {
    const pinned: Record<string, number> = { ...next.pinned };
    for (const [axis, value] of Object.entries(persisted.pinned)) {
      if (!names.has(axis)) continue;
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      if (axis === next.xInput || axis === next.yInput) continue;
      pinned[axis] = value;
    }
    // x/y choices may differ from the default split — drop pins that landed on the axes.
    delete pinned[next.xInput];
    delete pinned[next.yInput];
    next.pinned = pinned;
  }
  if (typeof persisted.slabFraction === "number" && Number.isFinite(persisted.slabFraction)) {
    next.slabFraction = Math.min(1, Math.max(0, persisted.slabFraction));
  }
  if (typeof persisted.selectedOutput === "string" && persisted.selectedOutput) {
    next.selectedOutput = persisted.selectedOutput;
  }
  if (persisted.evidenceMode === "raw" || persisted.evidenceMode === "compatible" || persisted.evidenceMode === "corrected") {
    next.evidenceMode = persisted.evidenceMode;
  }
  if (persisted.view3d && typeof persisted.view3d === "object") {
    const v = persisted.view3d as Partial<StudyView3d>;
    next.view3d = {
      yaw: Number.isFinite(v.yaw) ? (v.yaw as number) : DEFAULT_VIEW3D.yaw,
      pitch: Number.isFinite(v.pitch) ? (v.pitch as number) : DEFAULT_VIEW3D.pitch,
      zoom: Number.isFinite(v.zoom) ? (v.zoom as number) : DEFAULT_VIEW3D.zoom,
      panX: Number.isFinite(v.panX) ? (v.panX as number) : DEFAULT_VIEW3D.panX,
      panY: Number.isFinite(v.panY) ? (v.panY as number) : DEFAULT_VIEW3D.panY,
    };
  }
  return next;
}

function readPersistedCursor(key: string): Partial<PersistedCursor> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(studyCursorStorageKey(key));
    return raw ? (JSON.parse(raw) as Partial<PersistedCursor>) : null;
  } catch {
    return null;
  }
}

// The ORIGINAL persisted snapshot, cached on the first read per key
// (adversarial-review fix, finding: init against the fallback plane before
// campaign axes load used to drop persisted campaign/geometry cursors on
// every reload). Even if an early patch overwrites localStorage with the
// fallback-validated cursor, reconciliation re-hydrates from THIS snapshot
// once the real axis set arrives.
const rawPersistedCache = new Map<string, Partial<PersistedCursor> | null>();

function rawPersistedFor(key: string): Partial<PersistedCursor> | null {
  if (!rawPersistedCache.has(key)) rawPersistedCache.set(key, readPersistedCursor(key));
  return rawPersistedCache.get(key) ?? null;
}

/** Order-independent identity of an axis set (names + modes) — a cursor is
 *  re-reconciled whenever ensure() sees a DIFFERENT signature (campaign axes
 *  arriving after the fallback plane, a campaign swap, an axis-shape change). */
function axisSignature(inputs: readonly AxisSpec[]): string {
  return inputs
    .map((a) => `${a.name}:${a.mode}`)
    .sort()
    .join("|");
}

/** Reconcile an EXISTING in-session cursor with a new axis set. Two regimes:
 *
 *  - slice UNTOUCHED (the user never chose axes/pins this session): the
 *    hydrated persisted slice wins wholesale — the in-session values are just
 *    fallback-plane validation artifacts, and adopting them would re-drop the
 *    persisted campaign cursor the moment the real axes arrive (the finding);
 *  - slice TOUCHED: the user's in-session choices win wherever still valid
 *    under the new axes; invalid ones remap (no first-writer-wins staleness).
 *
 *  Pins live only on RANGE axes that are not x/y — pins on held/unknown axes
 *  prune (their value comes from the axis metadata, not the cursor). */
function reconcileStudyCursor(
  hydrated: StudyCursorState,
  current: StudyCursorState,
  inputs: readonly AxisSpec[],
  sliceTouched: boolean,
): StudyCursorState {
  const rangeNames = new Set(inputs.filter((a) => a.mode === "range").map((a) => a.name));
  const xInput = sliceTouched && rangeNames.has(current.xInput) ? current.xInput : hydrated.xInput;
  const yCandidate = sliceTouched && rangeNames.has(current.yInput) && current.yInput !== xInput
    ? current.yInput
    : hydrated.yInput !== xInput && rangeNames.has(hydrated.yInput)
      ? hydrated.yInput
      : firstOtherRangeAxis(inputs, xInput);
  const merged = sliceTouched ? { ...hydrated.pinned, ...current.pinned } : { ...current.pinned, ...hydrated.pinned };
  const pinned: Record<string, number> = {};
  for (const [axis, value] of Object.entries(merged)) {
    if (!rangeNames.has(axis)) continue; // held/unknown axes carry no pin
    if (axis === xInput || axis === yCandidate) continue;
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    pinned[axis] = value;
  }
  return {
    ...current, // in-session slab/output/evidence/selection/hover/camera survive
    xInput,
    yInput: yCandidate,
    pinned,
  };
}

// Debounced write-through: pin-slider drags patch the cursor per frame — batch
// the localStorage writes rather than hitting the disk 60×/s.
const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();
const PERSIST_DEBOUNCE_MS = 250;

function schedulePersist(key: string, cursor: StudyCursorState): void {
  if (typeof window === "undefined") return;
  const existing = persistTimers.get(key);
  if (existing) clearTimeout(existing);
  persistTimers.set(
    key,
    setTimeout(() => {
      persistTimers.delete(key);
      try {
        const snapshot: PersistedCursor = {
          xInput: cursor.xInput,
          yInput: cursor.yInput,
          pinned: cursor.pinned,
          slabFraction: cursor.slabFraction,
          selectedOutput: cursor.selectedOutput,
          evidenceMode: cursor.evidenceMode,
          view3d: cursor.view3d,
        };
        window.localStorage.setItem(studyCursorStorageKey(key), JSON.stringify(snapshot));
      } catch {
        // Best-effort persistence — quota/security failures must not break the UI.
      }
    }, PERSIST_DEBOUNCE_MS),
  );
}

type StudyStore = {
  byKey: Record<string, StudyCursorState>;
  /** Axis-set signature each cursor was last reconciled against. */
  axisSigByKey: Record<string, string>;
  /** Whether the USER changed slice geometry (axes/pins) this session —
   *  distinguishes real choices from fallback-validation artifacts. */
  sliceTouchedByKey: Record<string, boolean>;
  ensure: (key: string, inputs: readonly AxisSpec[]) => void;
  patch: (key: string, patch: Partial<StudyCursorState>) => void;
};

const useStudyStore = create<StudyStore>((set) => ({
  byKey: {},
  axisSigByKey: {},
  sliceTouchedByKey: {},
  ensure: (key, inputs) =>
    set((s) => {
      // Same guard as the per-campaign store above: never initialise (and so
      // never persist) a cursor before axis metadata loads — a blank
      // { xInput: '' } slice would stick and hang every surface query.
      const hasAxes = inputs.some((a) => a.mode === "range");
      if (!hasAxes) return s;
      const sig = axisSignature(inputs);
      const existing = s.byKey[key];
      // Fast path: same axis shape as last time and a live cursor — nothing to do.
      if (existing && existing.xInput && s.axisSigByKey[key] === sig) return s;
      const hydrated = hydrateStudyCursor(defaultStudyCursor(inputs), rawPersistedFor(key), inputs);
      // A richer/different axis set arriving later (campaign axes after the
      // fallback plane, campaign swaps) RE-RECONCILES instead of first-writer-
      // wins: persisted values return where the new axes support them, and
      // in-session USER choices survive wherever still valid (adversarial-
      // review findings: persisted campaign cursors dropped on reload; stale
      // x/y left on axes the campaign now holds).
      const cursor = existing && existing.xInput
        ? reconcileStudyCursor(hydrated, existing, inputs, s.sliceTouchedByKey[key] ?? false)
        : hydrated;
      return {
        ...s,
        byKey: { ...s.byKey, [key]: cursor },
        axisSigByKey: { ...s.axisSigByKey, [key]: sig },
      };
    }),
  patch: (key, patch) =>
    set((s) => {
      const current = s.byKey[key];
      if (!current) return s;
      const next: StudyCursorState = {
        ...current,
        ...patch,
        pinned: patch.pinned ? { ...current.pinned, ...patch.pinned } : current.pinned,
        view3d: patch.view3d ? { ...current.view3d, ...patch.view3d } : current.view3d,
      };
      // Only durable-field changes touch localStorage; hover moves stay in memory.
      if (PERSISTED_FIELDS.some((f) => f in patch)) schedulePersist(key, next);
      // Axis/pin patches are REAL user slice choices — remember, so a later
      // axis-set reconcile lets them win over the persisted snapshot.
      const touchesSlice = "xInput" in patch || "yInput" in patch || "pinned" in patch;
      return {
        ...s,
        byKey: { ...s.byKey, [key]: next },
        sliceTouchedByKey: touchesSlice
          ? { ...s.sliceTouchedByKey, [key]: true }
          : s.sliceTouchedByKey,
      };
    }),
}));

/** Deterministic cache key for the current slice: sorted pins + fixed float
 *  formatting so react-query keys are byte-identical for identical slices. */
export function studySliceKey(
  cursor: Pick<StudyCursorState, "xInput" | "yInput" | "pinned" | "slabFraction">,
): string {
  const fmt = (v: number): string => (Number.isFinite(v) ? String(Number(v.toFixed(6))) : "nan");
  const pins = Object.keys(cursor.pinned)
    .sort()
    .map((k) => `${k}:${fmt(cursor.pinned[k]!)}`)
    .join(",");
  return `x=${cursor.xInput}|y=${cursor.yInput}|slab=${fmt(cursor.slabFraction)}|pins=${pins}`;
}

/** The cursor snapshot subscribing panels see. hoverCursor is deliberately
 *  EXCLUDED: the crosshair moves at pointer rate, and a whole-cursor
 *  subscription would re-render every plotly/three panel per mousemove
 *  (the WebKit fps trap, §7). Crosshair layers subscribe via useStudyHover. */
export type StudyCursorSnapshot = Omit<StudyCursorState, "hoverCursor">;

export type UseStudyCursorResult = {
  cursor: StudyCursorSnapshot;
  patchCursor: (patch: Partial<StudyCursorState>) => void;
  /** Deterministic slice cache key (see studySliceKey). */
  sliceKey: string;
  /** The store key this cursor lives under (projectId::branch). */
  cursorKey: string;
};

/**
 * The Study Cursor hook — one shared cursor per (projectId, branch). Panels
 * call this with whatever axis metadata they have (campaign axes, project
 * defaults); the FIRST caller with real range axes initialises the cursor and
 * everyone else subscribes to the same state. Subscribes shallowly to every
 * field EXCEPT the pointer-rate hoverCursor (see StudyCursorSnapshot).
 */
export function useStudyCursor(
  projectId: string,
  branch: string,
  inputs: readonly AxisSpec[],
): UseStudyCursorResult {
  const key = studyCursorKey(projectId, branch);
  const ensure = useStudyStore((s) => s.ensure);
  const patch = useStudyStore((s) => s.patch);
  const stored = useStudyStore(
    useShallow((s) => {
      const c = s.byKey[key];
      if (!c) return undefined;
      const snapshot: StudyCursorSnapshot = {
        xInput: c.xInput,
        yInput: c.yInput,
        pinned: c.pinned,
        slabFraction: c.slabFraction,
        selectedOutput: c.selectedOutput,
        evidenceMode: c.evidenceMode,
        selectedPoint: c.selectedPoint,
        view3d: c.view3d,
      };
      return snapshot;
    }),
  );

  useEffect(() => {
    ensure(key, inputs);
  }, [key, inputs, ensure]);

  const fallback = useMemo(() => defaultStudyCursor(inputs), [inputs]);
  const cursor = stored ?? fallback;
  // Stable identity: consumers memoize handlers/overlays on patchCursor, so a
  // fresh closure per render would defeat their React.memo/useMemo guards.
  const patchCursor = useCallback((p: Partial<StudyCursorState>) => patch(key, p), [patch, key]);

  return {
    cursor,
    patchCursor,
    sliceKey: studySliceKey(cursor),
    cursorKey: key,
  };
}

/** Ensure-only: initialise the cursor for a key WITHOUT subscribing to it —
 *  for heavy parents (the workbench surface) that only write via
 *  patchStudyCursor / read via targeted useStudyCursorValue selectors. */
export function useEnsureStudyCursor(
  projectId: string,
  branch: string,
  inputs: readonly AxisSpec[],
): string {
  const key = studyCursorKey(projectId, branch);
  const ensure = useStudyStore((s) => s.ensure);
  useEffect(() => {
    ensure(key, inputs);
  }, [key, inputs, ensure]);
  return key;
}

/** Pointer-rate hover subscription — mount ONLY inside cheap crosshair layers. */
export function useStudyHover(projectId: string, branch: string): { x: number; y: number } | null {
  const key = studyCursorKey(projectId, branch);
  return useStudyStore((s) => s.byKey[key]?.hoverCursor ?? null);
}

/** Targeted single-field subscription (e.g. the parent surface watching only
 *  selectedPoint) — avoids re-rendering a heavy tree on unrelated cursor moves. */
export function useStudyCursorValue<T>(
  projectId: string,
  branch: string,
  selector: (cursor: StudyCursorState | undefined) => T,
): T {
  const key = studyCursorKey(projectId, branch);
  return useStudyStore((s) => selector(s.byKey[key]));
}

/** Imperative, non-subscribing patch — for callbacks in components that must
 *  not re-render on cursor changes (e.g. the workbench surface writing a solve
 *  back as the selected point). No-ops until the cursor is initialised. */
export function patchStudyCursor(
  projectId: string,
  branch: string,
  patch: Partial<StudyCursorState>,
): void {
  useStudyStore.getState().patch(studyCursorKey(projectId, branch), patch);
}

/** Reset seam for tests: clears in-memory cursors, the raw persisted-snapshot
 *  cache and pending persist timers. */
function resetStudyCursorStore(): void {
  for (const t of persistTimers.values()) clearTimeout(t);
  persistTimers.clear();
  rawPersistedCache.clear();
  useStudyStore.setState({ byKey: {}, axisSigByKey: {}, sliceTouchedByKey: {} });
}

// Exposed for tests.
export const __internal = { useSliceStore, useStudyStore, resetStudyCursorStore };
